import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
import { LoadingOverlay } from "../components/ui/loading-overlay";
import API_URL from "../config"; // ✅ Importa la URL del backend

export const createSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // Reemplaza caracteres no alfanuméricos con guiones
    .replace(/^-+|-+$/g, ""); // Elimina guiones al inicio o final
};

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

// Función para limpiar los datos de sesión
function clearSession() {
  const currentUserId = localStorage.getItem("current_user_id");

  if (currentUserId) {
    localStorage.removeItem(`access_token_${currentUserId}`);
    localStorage.removeItem(`refresh_token_${currentUserId}`);
    localStorage.removeItem("current_user_id");
  }
}

let alertShown = false; // Variable global para evitar múltiples alertas
export async function fetchWithAuthUpload(url, options = {}) {
  console.log(`🔵 Llamada a fetchWithAuthUpload: ${url}`);
  const requiresAuth = !options.public;

  if (requiresAuth) {
    const currentUserId = localStorage.getItem("current_user_id");

    if (!currentUserId) {
      console.warn("No se encontró current_user_id en localStorage.");
      toast.error("No hay un usuario autenticado. Inicia sesión nuevamente.");
      clearSession();
      alertShown = true; // Evita futuras alertas
      window.location.href = "/login";
      return;
    }

    let token = localStorage.getItem(`access_token_${currentUserId}`);
    options.headers = {
      Authorization: `Bearer ${token}`,
    };

    // Actualizar la actividad solo si no se ha actualizado en las últimas 24 horas
    const lastActivityUpdate = localStorage.getItem("lastActivityUpdate");
    const now = Date.now();
    const oneDayInMs = 12 * 60 * 60 * 1000; // 24 horas en milisegundos

    if (!lastActivityUpdate || now - lastActivityUpdate > oneDayInMs) {
      try {
        const response = await fetch(`${API_URL}/session/update_activity`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          localStorage.setItem("lastActivityUpdate", now); // Actualizar el registro de última actividad
        } else {
          console.error("No se pudo actualizar la actividad del usuario");
        }
      } catch (error) {
        console.error("Error al actualizar la actividad del usuario:", error);
      }
    }
  }

  // **⚠️ Importante:** No forzar `Content-Type`, ya que `FormData` lo maneja automáticamente
  delete options.headers["Content-Type"];

  let response = await fetch(url, options);
  console.log(`🔵 Respuesta recibida de ${url}:`, response.status);

  if (response.status === 401 && requiresAuth) {
    const currentUserId = localStorage.getItem("current_user_id");
    const refreshToken = localStorage.getItem(`refresh_token_${currentUserId}`);

    const refreshResponse = await fetch(`${API_URL}/user/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${refreshToken}`,
      },
    });

    if (refreshResponse.ok) {
      const refreshData = await refreshResponse.json();
      localStorage.setItem(
        `access_token_${currentUserId}`,
        refreshData.access_token
      );

      options.headers.Authorization = `Bearer ${refreshData.access_token}`;
      response = await fetch(url, options);
    } else {
      toast.error("Sesión expirada. Inicia sesión nuevamente.");
      clearSession();
      window.location.href = "/login";
      return;
    }
  }

  return response;
}

export async function fetchWithAuth(url, options = {}) {
  const requiresAuth = !options.public;

  if (requiresAuth) {
    const currentUserId = localStorage.getItem("current_user_id");

    if (!currentUserId) {
      console.warn("No se encontró current_user_id en localStorage.");
      toast.error("No hay un usuario autenticado. Inicia sesión nuevamente.");
      clearSession();
      alertShown = true; // Evita futuras alertas
      window.location.href = "/login";
      return;
    }

    const token = localStorage.getItem(`access_token_${currentUserId}`);
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    // Actualizar la actividad solo si no se ha actualizado en las últimas 24 horas
    const lastActivityUpdate = localStorage.getItem("lastActivityUpdate");
    const now = Date.now();
    const oneDayInMs = 12 * 60 * 60 * 1000; // 24 horas en milisegundos

    if (!lastActivityUpdate || now - lastActivityUpdate > oneDayInMs) {
      try {
        const response = await fetch(`${API_URL}/session/update_activity`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          localStorage.setItem("lastActivityUpdate", now); // Actualizar el registro de última actividad
        } else {
          console.error("No se pudo actualizar la actividad del usuario");
        }
      } catch (error) {
        console.error("Error al actualizar la actividad del usuario:", error);
      }
    }
  }

  // Configura Content-Type si la solicitud tiene un cuerpo JSON
  if (options.body && !options.headers["Content-Type"]) {
    options.headers = {
      ...options.headers,
      "Content-Type": "application/json",
    };
  }

  let response = await fetch(url, options);

  if (response.status === 401 && requiresAuth) {
    const currentUserId = localStorage.getItem("current_user_id");
    const refreshToken = localStorage.getItem(`refresh_token_${currentUserId}`);

    const refreshResponse = await fetch(`${API_URL}/user/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${refreshToken}`,
      },
    });

    if (refreshResponse.ok) {
      const refreshData = await refreshResponse.json();
      localStorage.setItem(
        `access_token_${currentUserId}`,
        refreshData.access_token
      );

      options.headers.Authorization = `Bearer ${refreshData.access_token}`;
      response = await fetch(url, options);
    } else {
      toast.error("Sesión expirada. Inicia sesión nuevamente.");
      clearSession();
      window.location.href = "/login";
      return;
    }
  }

  return response;
}

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [redirectingToLogin, setRedirectingToLogin] = useState(false); // Estado para evitar múltiples redirecciones

  // useEffect para cargar la información del usuario al cargar la app si hay un token
  useEffect(() => {
    const currentUserId = localStorage.getItem("current_user_id");
    const accessToken = localStorage.getItem(`access_token_${currentUserId}`);
    // 🚫 Si no hay usuario autenticado, no llamar a fetchUserInfo
    if (!currentUserId) {
      console.log(
        "🔴 No hay usuario autenticado. No se ejecuta fetchUserInfo."
      );
      setIsLoading(false);

      return;
    }

    if (currentUserId && accessToken) {
      fetchUserInfo().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchUserInfo = async () => {
    const currentUserId = localStorage.getItem("current_user_id");

    // 🚫 Si no hay usuario autenticado, no hacer la petición
    if (!currentUserId) {
      console.log("🔴 No hay usuario autenticado. Deteniendo fetchUserInfo.");
      setUser(null); // 🔹 Forzar `setUser(null)` para asegurar que React actualice el estado
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_URL}/user/user_info`);

      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        console.error("❌ Error al cargar la información básica del usuario");
        clearSession();
      }
    } catch (error) {
      console.error("❌ Error en fetchUserInfo:", error.message);
      clearSession();
    }
  };

  const logout = async () => {
    console.log("Cerrando sesión...");
    try {
      const currentUserId = localStorage.getItem("current_user_id");
      if (currentUserId) {
        const response = await fetch(`${API_URL}/user/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id: currentUserId || "" }),
        });

        if (response.ok) {
          console.log("✅ Logout exitoso en backend");
          setUser(null);
          clearSession(); // 🔹 Solo limpiar la sesión si el backend confirma el logout

          setRedirectingToLogin(true); // Evita redirecciones múltiples
        } else {
          console.error("Error en la respuesta del backend al cerrar sesión");
        }
      }
    } catch (error) {
      console.error("Error al cerrar sesión:", error.message);
    }
  };

  // Si está cargando, mostrar el componente Cargando
  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        logout,
        fetchUserInfo,
        isLoading,
        redirectingToLogin,
        setRedirectingToLogin,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
